import React, { useState, useEffect } from 'react';
import Popover from '@teetsh/app/src/components/Popover';
import tw from 'twin.macro';
import Button from '@teetsh/app/src/components/Buttons/Button';
import DeleteButton from '@teetsh/app/src/components/Buttons/DeleteButton';
import InputGroup from '@teetsh/app/src/components/Form/InputGroup';
import { CustomFieldDefinition } from '@teetsh/app/src/types/customField';
import useDeleteElevesCustomField from '@teetsh/app/src/queries/eleves/customFields/useDeleteElevesCustomField';
import { useTranslation } from 'react-i18next';
import { getPluralName } from 'utils/getPluralName';

interface Props {
  children: React.ReactNode;
  onChange: (v: CustomFieldDefinition) => void;
  value: CustomFieldDefinition;
  center?: boolean;
  withoutBorders?: boolean;
  className?: string;
}

export function InlineFormGroup(props: Props) {
  const { children, onChange, value, className, withoutBorders } = props;
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const deleteCustomField = useDeleteElevesCustomField();
  const [name, setName] = useState(value.name);
  const [pluralName, setPluralName] = useState(value.pluralName);
  const { t } = useTranslation(['common']);
  useEffect(() => {
    if (value.name !== name) {
      setName(value.name);
    }
  }, [value.name, value.id]);
  const enablePlural = value.type === 'multiselect' || value.type === 'select';

  return (
    <div
      css={[
        tw`sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start`,
        !withoutBorders && tw`sm:border-t sm:border-neutral-quiet sm:pt-4`,
        props.center && tw`sm:items-center`,
      ]}
      className={className}
    >
      <Popover portal onClose={() => setIsRenameOpen(false)}>
        <Popover.Button alignText="left" minimal>
          {value.name}
        </Popover.Button>
        <Popover.Panel size="auto">
          <div css={['max-height: 50vh;', tw`overflow-auto`]}>
            {!isRenameOpen && (
              <>
                <Button onClick={() => setIsRenameOpen(true)} minimal fill>
                  {t('common:buttons.rename')}
                </Button>
                <DeleteButton
                  portal
                  minimal
                  fill
                  withConfirmation
                  onClick={() =>
                    deleteCustomField({
                      customFieldId: value.id,
                    })
                  }
                >
                  {t('common:buttons.delete')}
                </DeleteButton>
              </>
            )}
            {isRenameOpen && (
              <div tw="flex flex-col m-2">
                <InputGroup
                  label={t('common:components.customFields.fieldNameLabel')}
                  autoFocus
                  value={name}
                  onChange={v => {
                    const newPluralName = getPluralName({
                      prevSingularName: name,
                      nextSingularName: v,
                      prevPluralName: pluralName,
                    });
                    setPluralName(newPluralName);
                    setName(v);
                  }}
                  onBlur={() => onChange({ ...value, name, pluralName })}
                />
                {enablePlural && (
                  <InputGroup
                    label={t(
                      'common:components.customFields.fieldNamePluralLabel',
                    )}
                    value={pluralName || ''}
                    onChange={v => setPluralName(v)}
                    onBlur={() => onChange({ ...value, pluralName })}
                  />
                )}
              </div>
            )}
          </div>
        </Popover.Panel>
      </Popover>
      <div tw="mt-1 sm:mt-0 sm:col-span-2">{children}</div>
    </div>
  );
}
